/**
 * visibility.helpers — pure "should this Instructions section render?" rules.
 *
 * Same shape and the same degradation ladder as `resolveMeetingModes`
 * (`meeting.helpers.ts`), because the failure mode is identical: a
 * section the backend never advertised is an offer the user can accept and the
 * place-order call then rejects (or, worse, silently ignores).
 *
 *   - `loading` / `idle` → do NOT render — never flash a section in that the
 *     resolved response may take straight back out,
 *   - `error`            → DO render (fail OPEN) — we cannot know what the BE
 *     offers, and blocking someone from ordering is worse than an over-offer,
 *   - `ready`            → exactly what the response advertises.
 *
 * Kept pure (no React / store / legacy provider) so both rules are unit-testable
 * without mounting the step.
 */

import type { MeetingMode } from 'models/purchaseFlowRevamp'

import type { InstructionCatalogueStatus } from './useInstructionCatalogue.types'

/**
 * W-11 — visibility of the "Book a favourite creative" section.
 *
 * The section used to render unconditionally, so every editing / staging /
 * documents cart offered a creative booking the response carried no `CT_BOOKING`
 * instruction type for.
 *
 * @param advertised `true` when the response carries ≥1 CT_BOOKING option.
 *   `undefined` = the source cannot answer (legacy Path A) ⇒ fail open.
 * @param status instruction-catalogue status.
 * @param hasSelection `true` when the draft already holds a `ctBookingId` — keep
 *   the section visible so a resumed selection stays reachable/clearable rather
 *   than becoming invisible state (mirrors `resolveMeetingModes`' selected-mode
 *   rule).
 *
 * @example
 * resolveCtBookingVisible(false, 'ready')          // false — BE advertised none
 * resolveCtBookingVisible(false, 'error')          // true  — fail open
 * resolveCtBookingVisible(false, 'ready', true)    // true  — resumed selection
 */
export const resolveCtBookingVisible = (
  advertised: boolean | undefined,
  status: InstructionCatalogueStatus,
  hasSelection = false
): boolean => {
  if (status === 'loading' || status === 'idle') return false
  if (status === 'error') return true
  return hasSelection || (advertised ?? true)
}

/**
 * V-06 — visibility of the meeting card's site-access framing + mode picker.
 *
 * Editing / staging / documents carts come back with an EMPTY `ORGANIZATION`
 * option list (see `.swagger.local/REAL-instruction-options-sample-20260811.json`),
 * yet the card still rendered its "Tell us how the creative gets on site"
 * framing above zero modes.
 *
 * Driven off the already-status-aware `availableModes` (`resolveMeetingModes`)
 * rather than the raw bucket, so every DF-I9 rule carries over for free — most
 * importantly a mode restored from a draft keeps the picker visible even after
 * the BE stops offering it.
 *
 * The `loading` branch returns `true` on purpose: the card owns its own spinner
 * (`isModesLoading`), and hiding the framing for one frame would make the whole
 * section jump on every refetch.
 *
 * @example
 * resolveSiteAccessVisible([], 'ready')                     // false — nothing to offer
 * resolveSiteAccessVisible(['KEY_PICKUP'], 'ready')          // true
 * resolveSiteAccessVisible([], 'loading')                   // true — keep framing + spinner
 */
export const resolveSiteAccessVisible = (
  availableModes: ReadonlyArray<MeetingMode>,
  status: InstructionCatalogueStatus
): boolean => {
  if (status === 'loading' || status === 'idle') return true
  if (status === 'error') return true
  return availableModes.length > 0
}

/**
 * V-06 — visibility of the meeting card as a whole.
 *
 * With no site access on offer the card has nothing to ask: it would decide HOW
 * the creative reaches a property the backend says needs no visit.
 *
 * The old `effectiveScheduledByCT === false` escape hatch is gone along with the
 * checkbox it protected. It existed so a REQUIRED meeting could still be
 * satisfied when the response carried no organization options — a shape that
 * cannot occur once the step only appears for carts needing a creative on site.
 * If it does occur the section stays hidden and the step cannot be completed:
 * a skip-rule bug to fix at source, not to paper over with a synthetic
 * "no meeting needed" option.
 *
 * @example
 * resolveMeetingSectionVisible([], 'ready')            // false — nothing to offer
 * resolveMeetingSectionVisible(['ON_SITE'], 'ready')   // true
 */
export const resolveMeetingSectionVisible = (
  availableModes: ReadonlyArray<MeetingMode>,
  status: InstructionCatalogueStatus
): boolean => resolveSiteAccessVisible(availableModes, status)

/**
 * Whether the meeting cards may show yet. When the step asks "fixed or flexible
 * booking" first, that answer decides which access modes are valid, so showing
 * the cards before it offers every mode at once. Where the question is not asked
 * (no scheduling section for this client or cart) nothing waits on it.
 *
 * @example
 * resolveMeetingAwaitsSchedule(true, undefined)   // true — ask fixed/flexible first
 * resolveMeetingAwaitsSchedule(true, 'flexible')  // false
 * resolveMeetingAwaitsSchedule(false, undefined)  // false — nothing to wait for
 */
export const resolveMeetingAwaitsSchedule = (
  scheduleAsked: boolean,
  scheduleMethod: 'fixed' | 'flexible' | undefined
): boolean => scheduleAsked && scheduleMethod === undefined

/**
 * Whether the step asks "fixed or flexible booking". It is where the shoot date
 * is entered, so every cart that sends someone on site gets it, on any plan —
 * only choosing the creative inside it is paid. A cart with the on-site area
 * report is booked by the technician afterwards, so it has nothing to ask, nor a
 * creative to choose.
 *
 * @example
 * resolveScheduleSectionVisible(true, false, false)  // true — unsubscribed client, on-site cart
 * resolveScheduleSectionVisible(false, false, false) // false — no visit
 * resolveScheduleSectionVisible(true, true, true)    // false — on-site area report in the cart
 */
export const resolveScheduleSectionVisible = (
  siteVisit: boolean,
  ctBookingOffered: boolean,
  hasMeasurement: boolean
): boolean => (siteVisit || ctBookingOffered) && !hasMeasurement
